import React from 'react';
import { ConnectivityBanner, useOnlineStatus, useResponsive } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';

import { useTranslation } from '../../i18n';
import { BRAND_CANVAS, BRAND_HERO, ON_BRAND } from './brandSurface.js';

/**
 * The one chrome every pre-membership screen renders through.
 *
 * The clinic panel has four screens that a person sees before they are inside a
 * clinic — the auth transitional states, «شروع», the registration wizard and the
 * invitation landing — and until this component they were four hand-rolled
 * copies of the same idea: a white card on the brand gradient. No two agreed.
 * The card was 420, 440, 460 and 560 wide; its radius was 16 or 18; its padding
 * 28, 32 or 44; the gradient was written out by hand at three different angles;
 * and three of them declared their own bouncing-dot keyframes. A person signing
 * up walked through all four in ninety seconds and saw the product change shape
 * under them each time.
 *
 * So the geometry lives here, once — and since 2026-09-19 it is the DOCTOR
 * panel's onboarding card (doctor-app src/screens/Onboarding), which is the
 * reference for every pre-membership screen in Parsina:
 *
 *   • canvas   BRAND_CANVAS  145° navy-dark → navy → navy-light
 *   • band     BRAND_HERO    160°, a 40px frosted tile + 15/800 title + 11px subtitle
 *   • card     white, radius 18, `0 40px 100px` shadow, 520 wide, rises 24px in
 *   • padding  26 / 28 / 32 — the doctor's body padding
 *
 * The gradients are spelt from the navy tokens in `brandSurface.js`, so an
 * accent preset still recolours them.
 *
 * ── Why `title` is focused rather than merely rendered ──────────────────────
 *
 * These screens replace one another without a full page load, and several of
 * them are *steps* rather than routes. A screen reader is told nothing by a
 * React re-render, so the heading takes `tabIndex={-1}` and receives focus when
 * its text changes — which is also what puts a keyboard user at the top of the
 * new screen instead of wherever the last button happened to be.
 *
 * ── `align` ────────────────────────────────────────────────────────────────
 *
 * Centred by default — including «شروع», whose height is data-dependent: the
 * canvas has a `minHeight`, not a `height`, so a card taller than the viewport
 * grows the canvas and the PAGE scrolls instead of clipping. `top` is for the
 * wizard, whose card changes height step to step and would jump vertically if
 * it were re-centred on every step. Never give the card its own inner
 * scrollbar — that hides its primary action behind a gesture nobody makes on a
 * form.
 *
 * ── The mobile reduction (REV2 §A20) ───────────────────────────────────────
 *
 * At or below the product's single 768px breakpoint the card goes full-bleed:
 * no radius, no shadow, no gradient margin, top-aligned. A 560px card centred
 * in a 375px viewport is a card with 24px of gradient either side of it and its
 * submit button below the fold — the phone IS the card. The switch is
 * `useResponsive()` rather than a media query because the width is an inline
 * style, and one breakpoint for the whole product is the kit's rule.
 *
 * PROMOTION NOTE (packages/DESIGN.md rule 4): the doctor app draws this same
 * card inline in its onboarding screen. The right end state is one component in
 * `packages/ui` that both import — this one is deliberately free of clinic
 * vocabulary so that move is a file copy.
 */

/**
 * Whether the pre-membership screens may attempt a write.
 *
 * One `useOnlineStatus()` for the whole shell, shared through context. The hook
 * registers window listeners and owns a probe timer, so calling it once per
 * screen AND once in the shell would run two independent detectors that can
 * disagree — and would show the banner from one while the buttons obeyed the
 * other.
 *
 * ⚠︎ The hook returns an OBJECT (`{ state, isOnline, … }`), not the state
 * string. Comparing its return value against `ConnectionState.ONLINE` is always
 * false, which disables every submit button on every screen — with the banner
 * invisible, because `ConnectivityBanner` looks the object up in its variant map
 * and finds nothing. That is the shape of this bug when it comes back.
 */
const OfflineCtx = React.createContext(false);

// The doctor's two card widths: 420 for its boot card, 520 for onboarding.
// `lg` used to be 720 for the registration wizard; the wizard now fits the
// doctor's 520 like every other step a new user walks through.
const WIDTHS = {
  /** Transitional states: a sentence and one action. */
  sm: 420,
  /** A screen you read: «شروع», the invitation landing. */
  md: 520,
  /** A screen you fill in: the registration wizard. */
  lg: 520,
};

/** Material `domain` — the header tile's glyph, in the doctor's filled family. */
const ClinicGlyph = () => (
  <svg viewBox="0 0 24 24" fill={ON_BRAND.glyph} width="20" height="20" aria-hidden="true">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
  </svg>
);

export default function AuthShell({
  width = 'md',
  align = 'center',
  icon,
  title,
  subtitle,
  headerEnd,
  footer,
  utility,
  children,
  testId = 'auth-shell',
}) {
  const { t } = useTranslation();
  const { state: connection, isOnline, recovered, acknowledgeRecovery, check } = useOnlineStatus();
  const { isMobile } = useResponsive();
  const headingRef = React.useRef(null);
  const [probing, setProbing] = React.useState(false);

  // Announce the screen, not just paint it. Keyed on the title text so a step
  // change inside one route re-announces, which is the case a route-level
  // effect would miss entirely.
  const titleKey = typeof title === 'string' ? title : '';
  React.useEffect(() => {
    if (!titleKey) return;
    // Do NOT steal focus from a field that has already claimed it. The
    // registration form autofocuses its first input, which is the more useful
    // place to be — and a heading that grabs focus back on mount would make the
    // caret jump out of the box the moment the person started typing.
    const active = document.activeElement;
    if (active && active !== document.body && headingRef.current?.parentElement?.closest('main')?.contains(active)) return;
    headingRef.current?.focus();
  }, [titleKey]);

  return (
    <div
      data-testid={testId}
      style={{
        ...(isMobile ? {
          // At ≤768px the DOCUMENT cannot scroll: styles.css locks html/body and
          // fixes #root to the 100dvh viewport, and every in-app screen brings
          // its own scroll region (MobileScreen). This canvas is that region for
          // the pre-membership screens — without it the wizard on a 568px-tall
          // iPhone SE, or «شروع» with a few invitations, was clipped below the
          // fold with no gesture that reached its submit. `height: 100%` sizes
          // it to #root (the SMALL viewport on iOS), not `100vh` (the large
          // one, whose bottom sits under Safari's toolbar).
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
        } : { minHeight: '100vh' }),
        width: '100%',
        display: 'flex',
        // On a phone the card IS the screen, so it starts at the top rather
        // than floating in the middle of a viewport it fills anyway. Not
        // `stretch`: in a fixed-height flex line that clamps the card to the
        // viewport, and its `overflow: hidden` would clip the rest again.
        alignItems: isMobile || align === 'top' ? 'flex-start' : 'center',
        justifyContent: 'center',
        background: BRAND_CANVAS,
        padding: isMobile ? 0 : (align === 'top' ? '40px 16px' : '24px 16px'),
        boxSizing: 'border-box',
      }}
    >
      <main
        className={isMobile ? undefined : 'auth-shell-card'}
        style={{
          background: 'white',
          borderRadius: isMobile ? 0 : 18,
          boxShadow: isMobile ? 'none' : '0 40px 100px rgba(0,0,0,0.35)',
          width: isMobile ? '100%' : (WIDTHS[width] || WIDTHS.md),
          maxWidth: '100%',
          // At least the scroll region's height, so the white card still fills
          // a short screen; free to grow past it, which is what scrolls.
          minHeight: isMobile ? '100%' : undefined,
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined,
          overflow: 'hidden',
        }}
      >
        {title || subtitle || headerEnd ? (
          <div
            style={{
              background: BRAND_HERO,
              padding: '22px 28px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: ON_BRAND.tile,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              {icon || <ClinicGlyph />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {title ? (
                <h1
                  ref={headingRef}
                  tabIndex={-1}
                  style={{
                    margin: 0, fontSize: 15, fontWeight: 800, color: 'white',
                    outline: 'none', lineHeight: 1.5,
                  }}
                >
                  {title}
                </h1>
              ) : null}
              {subtitle ? (
                <div style={{ fontSize: 11, color: ON_BRAND.muted, lineHeight: 1.6 }}>
                  {subtitle}
                </div>
              ) : null}
            </div>
            {headerEnd ? <div style={{ flexShrink: 0 }}>{headerEnd}</div> : null}
          </div>
        ) : null}

        <div style={{ padding: '26px 28px 32px' }}>
          {/* One banner, mounted once, for every pre-membership screen. A switch
              or a submit while the connection is degraded is guaranteed to fail,
              and the screens disable their own mutations from the same signal
              (REV2 §A21). */}
          {!isOnline || recovered ? (
            <div style={{ marginBottom: 16 }}>
              <ConnectivityBanner
                state={connection}
                recovered={recovered}
                onAcknowledgeRecovery={acknowledgeRecovery}
                onRetry={async () => { setProbing(true); try { await check(); } finally { setProbing(false); } }}
                busy={probing}
                offlineLabel={t('common.connectivity.offline')}
                degradedLabel={t('common.connectivity.degraded')}
                restoredLabel={t('common.connectivity.restored')}
                retryLabel={t('common.actions.retry')}
                busyLabel={t('common.connectivity.checking')}
              />
            </div>
          ) : null}
          <OfflineCtx.Provider value={!isOnline}>{children}</OfflineCtx.Provider>
        </div>

        {footer ? (
          <div
            style={{
              borderTop: `1px solid ${C.border}`,
              padding: '20px 28px 28px',
            }}
          >
            {footer}
          </div>
        ) : null}
      </main>

      {utility ? (
        <div style={{ position: 'fixed', insetBlockEnd: 16, insetInlineEnd: 16 }}>{utility}</div>
      ) : null}
    </div>
  );
}

/**
 * True while a write from a pre-membership screen is certain to fail.
 *
 * Gates the SUBMIT, never the fields: a form the person is halfway through
 * filling in must survive a lift or a dead spot, and the banner above already
 * explains why the button will not go.
 */
export function useAuthShellOffline() {
  return React.useContext(OfflineCtx);
}
